import React, { useCallback, useEffect, useMemo, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useEnglishText } from "../../../text/useEnglishText.js";

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DownloadIcon from "@mui/icons-material/Download";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import PaidIcon from "@mui/icons-material/Paid";
import { AuthContext } from "../../../context/AuthContext";
import AdminPageHeader from "../../../components/common/AdminPageHeader";
import EmptyState from "../../../components/common/EmptyState";
import LoadingState from "../../../components/common/LoadingState";
import { ReceiptText } from "lucide-react";
import { Button } from "../../../components/ui/button";
import "../../../styles/BillingPayments.css";

import {
  fetchFinancialReport,
  fetchFinancialSummary,
  fetchInvoices,
  fetchProformas,
} from "../../../services/billingPaymentsService";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const formatMoney = (value, currency = "EUR") => {
  const amount = Number(value) || 0;

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "EUR",
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency || "EUR"}`;
  }
};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

function BillingPayments() {
  const { t } = useEnglishText();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [activeTab, setActiveTab] = useState("overview");

  const [invoices, setInvoices] = useState([]);
  const [proformas, setProformas] = useState([]);

  const [summary, setSummary] = useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [refreshError, setRefreshError] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date-desc");

  const [selectedDocument, setSelectedDocument] = useState(null);

  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const [reportFrom, setReportFrom] = useState("");
  const [reportTo, setReportTo] = useState("");
  const [reportType, setReportType] = useState("all");

  /* ------------------------------------------------------------------------ */
  /* Tabs                                                                     */
  /* ------------------------------------------------------------------------ */

  const tabs = useMemo(
    () => [
      {
        id: "overview",
        label: t("billing.tabs.overview"),
        icon: AssessmentIcon,
      },
      {
        id: "invoices",
        label: t("billing.tabs.invoices"),
        icon: ReceiptLongIcon,
      },
      {
        id: "proformas",
        label: t("billing.tabs.proformas"),
        icon: RequestQuoteIcon,
      },
      {
        id: "reports",
        label: t("billing.tabs.reports"),
        icon: AssessmentIcon,
      },
    ],
    [t],
  );

  /* ------------------------------------------------------------------------ */
  /* Status                                                                   */
  /* ------------------------------------------------------------------------ */

  const getStatusLabel = (status) => {
    if (!status) return "—";

    const normalizedStatus = String(status).toLowerCase();

    const statusKeys = {
      open: "billing.status.open",
      paid: "billing.status.paid",
      overdue: "billing.status.overdue",
      unpaid: "billing.status.unpaid",
      cancelled: "billing.status.cancelled",
      draft: "billing.status.draft",
      partiallypaid: "billing.status.partiallyPaid",
      partially_paid: "billing.status.partiallyPaid",
      pending: "billing.status.pending",
      processing: "billing.status.processing",
      failed: "billing.status.failed",
    };

    const translationKey = statusKeys[normalizedStatus];

    return translationKey ? t(translationKey) : status;
  };

  const getStatusClass = (status) => {
    return `billing-status billing-status-${String(status || "")
      .toLowerCase()
      .replaceAll("_", "-")}`;
  };

  /* ------------------------------------------------------------------------ */
  /* Load data                                                                */
  /* ------------------------------------------------------------------------ */

  const loadData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (silent) setRefreshError("");
      else setLoadError("");
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const [invoiceData, proformaData, summaryData] = await Promise.all([
        fetchInvoices(),
        fetchProformas(),
        fetchFinancialSummary(),
      ]);

      setInvoices(invoiceData || []);
      setProformas(proformaData || []);
      setSummary(summaryData || null);
    } catch (error) {
      console.error("Billing Center loading failed:", error);
      if (silent) setRefreshError(t("billing.refreshError", "Unable to refresh billing information."));
      else setLoadError(t("billing.loadError", "Unable to load billing information."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* ------------------------------------------------------------------------ */
  /* Documents                                                                */
  /* ------------------------------------------------------------------------ */

  const allDocuments = useMemo(
    () => [...invoices, ...proformas],
    [invoices, proformas],
  );

  const filteredDocuments = useMemo(() => {
    let result = [...allDocuments];

    const normalizedSearch = search.trim().toLowerCase();

    if (normalizedSearch) {
      result = result.filter((item) => {
        const searchable = [
          item.number,
          item.title,
          item.description,
          item.customer,
          item.type,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchable.includes(normalizedSearch);
      });
    }

    if (statusFilter !== "all") {
      result = result.filter((item) => item.status === statusFilter);
    }

    result.sort((a, b) => {
      if (sortBy === "date-desc") {
        return new Date(b.issueDate || 0) - new Date(a.issueDate || 0);
      }

      if (sortBy === "date-asc") {
        return new Date(a.issueDate || 0) - new Date(b.issueDate || 0);
      }

      if (sortBy === "amount-desc") {
        return Number(b.total || 0) - Number(a.total || 0);
      }

      if (sortBy === "amount-asc") {
        return Number(a.total || 0) - Number(b.total || 0);
      }

      return 0;
    });

    return result;
  }, [allDocuments, search, statusFilter, sortBy]);

  /* ------------------------------------------------------------------------ */
  /* Document groups                                                          */
  /* ------------------------------------------------------------------------ */

  const openInvoices = invoices.filter(
    (invoice) => invoice.status === "open" || invoice.status === "overdue",
  );

  const closedInvoices = invoices.filter(
    (invoice) => invoice.status === "paid" || invoice.status === "cancelled",
  );

  const unpaidProformas = proformas.filter(
    (proforma) => proforma.status === "unpaid" || proforma.status === "open",
  );

  const paidProformas = proformas.filter(
    (proforma) => proforma.status === "paid",
  );

  /* ------------------------------------------------------------------------ */
  /* Wallet                                                                   */
  /* ------------------------------------------------------------------------ */

  const goToWallet = (document) => {
    if (!document) return;

    navigate("/dashboard/wallet", {
      state: {
        source: "billing-payments",
        paymentDocument: document,
        paymentRef: document.id || document.number,
      },
    });
  };

  /* ------------------------------------------------------------------------ */
  /* Reports                                                                  */
  /* ------------------------------------------------------------------------ */

  const generateReport = async () => {
    try {
      setReportLoading(true);
      console.log();

    

      if (!user.LT_COUNTRY_ID) {
        console.error("Currency ID is missing from AuthContext user:", user);
        return;
      }

      const result = await fetchFinancialReport({
        from: reportFrom,
        to: reportTo,
        type: reportType || "ALL",
        ltCurrencyId: String(user.LT_COUNTRY_ID),
      });

      setReport(result);
    } catch (error) {
      console.error("Financial report failed:", error);
    } finally {
      setReportLoading(false);
    }
  };

  const exportReport = () => {
    if (!report?.rows?.length) return;

    const headers = [
      t("billing.table.date"),
      t("billing.table.document"),
      t("billing.table.type"),
      t("billing.document.description"),
      "Debit",
      "Credit",
      t("billing.table.status"),
    ];

    const rows = report.rows.map((row) => [
      row.date,
      row.document,
      row.type,
      row.description,
      row.debit,
      row.credit,
      row.status,
    ]);

    const csv = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "financial-report.csv";

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);
  };

  /* ------------------------------------------------------------------------ */
  /* Payment button                                                            */
  /* ------------------------------------------------------------------------ */

  const renderPaymentButton = (document) => {
    const payable =
      document.status === "open" ||
      document.status === "overdue" ||
      document.status === "unpaid";

    if (!payable) return null;

    return (
      <button
        type="button"
        className="billing-btn billing-btn-primary"
        onClick={() => goToWallet(document)}
      >
        <AccountBalanceWalletIcon fontSize="small" />

        {t("billing.actions.payViaWallet")}

        <ArrowForwardIcon fontSize="small" />
      </button>
    );
  };

  /* ------------------------------------------------------------------------ */
  /* Document row                                                              */
  /* ------------------------------------------------------------------------ */

  const renderDocumentRow = (document) => {
    const isInvoice = document.type === "invoice";

    const balance = Number(document.balance ?? document.total ?? 0);

    return (
      <div
        className="billing-document-row"
        key={document.id || document.number}
      >
        <div className="billing-document-main">
          <div className="billing-document-icon">
            {isInvoice ? <ReceiptLongIcon /> : <RequestQuoteIcon />}
          </div>

          <div>
            <button
              type="button"
              className="billing-document-number"
              onClick={() => setSelectedDocument(document)}
            >
              {document.number}
            </button>

            <div className="billing-document-title">{document.title}</div>

            <div className="billing-document-meta">
              {isInvoice
                ? t("billing.document.invoice")
                : t("billing.document.proforma")}
              {" • "}
              {t("billing.document.issued")} {formatDate(document.issueDate)}
            </div>
          </div>
        </div>

        <div className="billing-document-date">
          <span>{t("billing.document.dueDate")}</span>

          <strong>{formatDate(document.dueDate)}</strong>
        </div>

        <div className="billing-document-status">
          <span className={getStatusClass(document.status)}>
            {getStatusLabel(document.status)}
          </span>
        </div>

        <div className="billing-document-amount">
          <strong>
            {formatMoney(document.balance ?? document.total, document.currency)}
          </strong>

          <span>
            {balance > 0
              ? t("billing.document.outstanding")
              : t("billing.document.settled")}
          </span>
        </div>

        <div className="billing-document-actions">
          <button
            type="button"
            className="billing-icon-btn"
            title={t("billing.actions.viewDetails")}
            aria-label={t("billing.actions.viewDetails")}
            onClick={() => setSelectedDocument(document)}
          >
            <OpenInNewIcon fontSize="small" />
          </button>

          {renderPaymentButton(document)}
        </div>
      </div>
    );
  };

  /* ------------------------------------------------------------------------ */
  /* Documents renderer                                                        */
  /* ------------------------------------------------------------------------ */

  const renderDocuments = (
    documents,
    emptyTitle = t("billing.empty.title"),
    emptyDescription = t("billing.empty.description"),
  ) => {
    if (!documents.length) {
      return <EmptyState title={emptyTitle} description={emptyDescription} />;
    }

    return (
      <div className="billing-document-list">
        {documents.map(renderDocumentRow)}
      </div>
    );
  };

  /* ------------------------------------------------------------------------ */
  /* Loading                                                                   */
  /* ------------------------------------------------------------------------ */

  if (loading) {
    return (
      <div className="billing-page">
        <div className="billing-container">
          <AdminPageHeader icon={ReceiptText} title={t("billing.title")} description={t("billing.description")} />
          <LoadingState variant="card" label={t("billing.loading.title", "Loading billing information")} />
        </div>
      </div>
    );
  }

  if (loadError) {
    return <div className="billing-page"><div className="billing-container"><AdminPageHeader icon={ReceiptText} title={t("billing.title")} description={t("billing.description")} actions={<Button variant="outline" onClick={() => loadData()}>{t("billing.actions.refresh")}</Button>} /><div className="admin-data-error" role="alert">{loadError}</div></div></div>;
  }

  /* ------------------------------------------------------------------------ */
  /* Render                                                                    */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="billing-page">
      <div className="billing-container">
        {/* ---------------------------------------------------------------- */}
        {/* Header                                                           */}
        {/* ---------------------------------------------------------------- */}

        <AdminPageHeader
          icon={ReceiptText}
          title={t("billing.title")}
          description={t("billing.description")}
          actions={<Button variant="outline" onClick={() => loadData({ silent: true })} disabled={refreshing}>
            <RefreshIcon className={refreshing ? "billing-spin" : ""} aria-hidden="true" />
            {refreshing ? t("billing.actions.refreshing") : t("billing.actions.refresh")}
          </Button>}
        />
        {refreshError && <div className="admin-data-error" role="alert">{refreshError}</div>}

        {/* ---------------------------------------------------------------- */}
        {/* Summary                                                          */}
        {/* ---------------------------------------------------------------- */}

        <section className="billing-summary-grid">
          <div className="billing-summary-card">
            <div className="billing-summary-icon">
              <CurrencyExchangeIcon />
            </div>

            <div>
              <span>{t("billing.summary.totalInvoiced")}</span>

              <strong>
                {formatMoney(summary?.totalInvoiced, summary?.currency)}
              </strong>
            </div>
          </div>

          <div className="billing-summary-card billing-summary-success">
            <div className="billing-summary-icon">
              <CheckCircleIcon />
            </div>

            <div>
              <span>{t("billing.summary.totalPaid")}</span>

              <strong>
                {formatMoney(summary?.totalPaid, summary?.currency)}
              </strong>
            </div>
          </div>

          <div className="billing-summary-card billing-summary-warning">
            <div className="billing-summary-icon">
              <WarningAmberIcon />
            </div>

            <div>
              <span>{t("billing.summary.outstanding")}</span>

              <strong>
                {formatMoney(summary?.totalOutstanding, summary?.currency)}
              </strong>
            </div>
          </div>

          <div className="billing-summary-card billing-summary-accent">
            <div className="billing-summary-icon">
              <RequestQuoteIcon />
            </div>

            <div>
              <span>{t("billing.summary.unpaidProformas")}</span>

              <strong>
                {formatMoney(summary?.totalProforma, summary?.currency)}
              </strong>
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* Tabs                                                             */}
        {/* ---------------------------------------------------------------- */}

        <nav className="billing-tabs">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;

            return (
              <button
                type="button"
                key={tab.id}
                className={
                  activeTab === tab.id ? "billing-tab active" : "billing-tab"
                }
                onClick={() => setActiveTab(tab.id)}
              >
                <TabIcon fontSize="small" />

                {tab.label}
              </button>
            );
          })}
        </nav>

        {/* ---------------------------------------------------------------- */}
        {/* Overview                                                         */}
        {/* ---------------------------------------------------------------- */}

        {activeTab === "overview" && (
          <div className="billing-content">
            <section className="billing-overview-grid">
              {/* Payment Overview */}

              <div className="billing-panel">
                <div className="billing-panel-header">
                  <div>
                    <h2>{t("billing.summary.totalInvoiced")}</h2>

                    <p>{t("billing.description")}</p>
                  </div>
                </div>

                <div className="billing-overview-list">
                  {/* Open invoices */}

                  <button
                    type="button"
                    onClick={() => setActiveTab("invoices")}
                    className="billing-overview-item"
                  >
                    <span className="billing-overview-item-icon" aria-hidden="true">
                      <PendingActionsIcon fontSize="small" />
                    </span>

                    <span>
                      <strong>{t("billing.summary.openInvoices")}</strong>

                      <small>
                        {t("billing.reports.documentCount", {
                          count: openInvoices.length,
                        })}
                      </small>
                    </span>

                    <strong>
                      {formatMoney(
                        openInvoices.reduce(
                          (sum, item) =>
                            sum + Number(item.balance ?? item.total ?? 0),
                          0,
                        ),
                        summary?.currency,
                      )}
                    </strong>
                  </button>

                  {/* Closed invoices */}

                  <button
                    type="button"
                    onClick={() => setActiveTab("invoices")}
                    className="billing-overview-item"
                  >
                    <span className="billing-overview-item-icon" aria-hidden="true">
                      <TaskAltIcon fontSize="small" />
                    </span>

                    <span>
                      <strong>{t("billing.summary.closedInvoices")}</strong>

                      <small>
                        {t("billing.reports.documentCount", {
                          count: closedInvoices.length,
                        })}
                      </small>
                    </span>

                    <strong>
                      {formatMoney(
                        closedInvoices.reduce(
                          (sum, item) => sum + Number(item.total || 0),
                          0,
                        ),
                        summary?.currency,
                      )}
                    </strong>
                  </button>

                  {/* Unpaid proformas */}

                  <button
                    type="button"
                    onClick={() => setActiveTab("proformas")}
                    className="billing-overview-item"
                  >
                    <span className="billing-overview-item-icon" aria-hidden="true">
                      <MoneyOffIcon fontSize="small" />
                    </span>

                    <span>
                      <strong>{t("billing.summary.unpaidProformas")}</strong>

                      <small>
                        {t("billing.reports.documentCount", {
                          count: unpaidProformas.length,
                        })}
                      </small>
                    </span>

                    <strong>
                      {formatMoney(
                        unpaidProformas.reduce(
                          (sum, item) => sum + Number(item.total || 0),
                          0,
                        ),
                        summary?.currency,
                      )}
                    </strong>
                  </button>

                  {/* Paid proformas */}

                  <button
                    type="button"
                    onClick={() => setActiveTab("proformas")}
                    className="billing-overview-item"
                  >
                    <span className="billing-overview-item-icon" aria-hidden="true">
                      <PaidIcon fontSize="small" />
                    </span>

                    <span>
                      <strong>{t("billing.summary.paidProformas")}</strong>

                      <small>
                        {t("billing.reports.documentCount", {
                          count: paidProformas.length,
                        })}
                      </small>
                    </span>

                    <strong>
                      {formatMoney(
                        paidProformas.reduce(
                          (sum, item) => sum + Number(item.total || 0),
                          0,
                        ),
                        summary?.currency,
                      )}
                    </strong>
                  </button>
                </div>
              </div>

              {/* Quick Actions */}

              <div className="billing-panel">
                <div className="billing-panel-header">
                  <div>
                    <h2>{t("billing.quickActions.title")}</h2>

                    <p>{t("billing.description")}</p>
                  </div>
                </div>

                <div className="billing-quick-actions">
                  <button
                    type="button"
                    onClick={() => setActiveTab("invoices")}
                  >
                    <span>
                      <ReceiptLongIcon />
                    </span>

                    <div>
                      <strong>{t("billing.quickActions.invoices")}</strong>

                      <small>{t("billing.invoices.description")}</small>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setActiveTab("proformas")}
                  >
                    <span>
                      <RequestQuoteIcon />
                    </span>

                    <div>
                      <strong>{t("billing.quickActions.proformas")}</strong>

                      <small>{t("billing.proformas.description")}</small>
                    </div>
                  </button>

                  <button type="button" onClick={() => setActiveTab("reports")}>
                    <span>
                      <AssessmentIcon />
                    </span>

                    <div>
                      <strong>{t("billing.quickActions.reports")}</strong>

                      <small>{t("billing.reports.description")}</small>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/dashboard/wallet")}
                  >
                    <span>
                      <AccountBalanceWalletIcon />
                    </span>

                    <div>
                      <strong>{t("billing.quickActions.wallet")}</strong>

                      <small>{t("billing.payment.wallet")}</small>
                    </div>
                  </button>
                </div>
              </div>
            </section>

            {/* Recent Documents */}

            <section className="billing-panel billing-recent-panel">
              <div className="billing-panel-header">
                <div>
                  <h2>{t("billing.table.document")}</h2>

                  <p>{t("billing.description")}</p>
                </div>

                <button
                  type="button"
                  className="billing-text-btn"
                  onClick={() => setActiveTab("invoices")}
                >
                  {t("billing.actions.viewDetails")} →
                </button>
              </div>

              {renderDocuments(allDocuments.slice(0, 5))}
            </section>
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Invoices                                                         */}
        {/* ---------------------------------------------------------------- */}

        {activeTab === "invoices" && (
          <div className="billing-content">
            <section className="billing-panel">
              <div className="billing-panel-header">
                <div>
                  <h2>{t("billing.invoices.title")}</h2>

                  <p>{t("billing.invoices.description")}</p>
                </div>

                <div className="billing-count">
                  {t("billing.reports.documentCount", {
                    count: invoices.length,
                  })}
                </div>
              </div>

              <div className="billing-filter-bar">
                <div className="billing-search">
                  <SearchIcon fontSize="small" />

                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t("billing.filters.searchPlaceholder")}
                  />
                </div>

                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  aria-label={t("billing.filters.status")}
                >
                  <option value="all">
                    {t("billing.filters.allStatuses")}
                  </option>

                  <option value="open">{t("billing.filters.open")}</option>

                  <option value="overdue">
                    {t("billing.filters.overdue")}
                  </option>

                  <option value="paid">{t("billing.filters.paid")}</option>

                  <option value="cancelled">
                    {t("billing.filters.cancelled")}
                  </option>
                </select>

                <select
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value)}
                  aria-label={t("billing.filters.sort")}
                >
                  <option value="date-desc">
                    {t("billing.filters.newest")}
                  </option>

                  <option value="date-asc">
                    {t("billing.filters.oldest")}
                  </option>

                  <option value="amount-desc">
                    {t("billing.filters.highestAmount")}
                  </option>

                  <option value="amount-asc">
                    {t("billing.filters.lowestAmount")}
                  </option>
                </select>
              </div>

              {renderDocuments(
                filteredDocuments.filter((item) => item.type === "invoice"),
                t("billing.empty.invoices"),
                t("billing.empty.description"),
              )}
            </section>
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Proformas                                                        */}
        {/* ---------------------------------------------------------------- */}

        {activeTab === "proformas" && (
          <div className="billing-content">
            <section className="billing-panel">
              <div className="billing-panel-header">
                <div>
                  <h2>{t("billing.proformas.title")}</h2>

                  <p>{t("billing.proformas.description")}</p>
                </div>

                <div className="billing-proforma-summary">
                  <span>
                    {t("billing.proformas.unpaid")}{" "}
                    <strong>{unpaidProformas.length}</strong>
                  </span>

                  <span>
                    {t("billing.proformas.paid")}{" "}
                    <strong>{paidProformas.length}</strong>
                  </span>
                </div>
              </div>

              <div className="billing-filter-bar">
                <div className="billing-search">
                  <SearchIcon fontSize="small" />

                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t("billing.filters.searchPlaceholder")}
                  />
                </div>

                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  aria-label={t("billing.filters.status")}
                >
                  <option value="all">
                    {t("billing.filters.allStatuses")}
                  </option>

                  <option value="unpaid">{t("billing.filters.unpaid")}</option>

                  <option value="paid">{t("billing.filters.paid")}</option>
                </select>

                <select
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value)}
                  aria-label={t("billing.filters.sort")}
                >
                  <option value="date-desc">
                    {t("billing.filters.newest")}
                  </option>

                  <option value="date-asc">
                    {t("billing.filters.oldest")}
                  </option>

                  <option value="amount-desc">
                    {t("billing.filters.highestAmount")}
                  </option>

                  <option value="amount-asc">
                    {t("billing.filters.lowestAmount")}
                  </option>
                </select>
              </div>

              {renderDocuments(
                filteredDocuments.filter((item) => item.type === "proforma"),
                t("billing.empty.proformas"),
                t("billing.empty.description"),
              )}
            </section>
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Reports                                                          */}
        {/* ---------------------------------------------------------------- */}

        {activeTab === "reports" && (
          <div className="billing-content">
            <section className="billing-panel">
              <div className="billing-panel-header">
                <div>
                  <h2>{t("billing.reports.title")}</h2>

                  <p>{t("billing.reports.description")}</p>
                </div>
              </div>

              <div className="billing-report-filters">
                <label>
                  <span>{t("billing.reports.from")}</span>

                  <input
                    type="date"
                    value={reportFrom}
                    onChange={(event) => setReportFrom(event.target.value)}
                  />
                </label>

                <label>
                  <span>{t("billing.reports.to")}</span>

                  <input
                    type="date"
                    value={reportTo}
                    onChange={(event) => setReportTo(event.target.value)}
                  />
                </label>

                <label>
                  <span>{t("billing.reports.documentType")}</span>

                  <select
                    value={reportType}
                    onChange={(event) => setReportType(event.target.value)}
                  >
                    <option value="all">{t("billing.reports.all")}</option>

                    <option value="invoice">
                      {t("billing.reports.invoices")}
                    </option>

                    <option value="proforma">
                      {t("billing.reports.proformas")}
                    </option>

                    <option value="payment">
                      {t("billing.reports.payments")}
                    </option>
                  </select>
                </label>

                <button
                  type="button"
                  className="billing-btn billing-btn-primary billing-report-generate"
                  onClick={generateReport}
                  disabled={reportLoading}
                >
                  {reportLoading
                    ? t("billing.actions.generating")
                    : t("billing.actions.generateReport")}
                </button>
              </div>
            </section>

            {/* Generated Report */}

            {report && (
              <section className="billing-panel">
                <div className="billing-panel-header">
                  <div>
                    <h2>{t("billing.reports.generatedReport")}</h2>

                    <p>
                      {t("billing.reports.records", {
                        count: report.rows?.length || 0,
                      })}
                    </p>
                  </div>

                  <button
                    type="button"
                    className="billing-btn billing-btn-secondary"
                    onClick={exportReport}
                    disabled={!report.rows?.length}
                  >
                    <DownloadIcon fontSize="small" />

                    {t("billing.actions.exportCsv")}
                  </button>
                </div>

                {report.rows?.length ? (
                  <div className="billing-report-table-wrap">
                    <table className="billing-report-table">
                      <thead>
                        <tr>
                          <th>{t("billing.table.date")}</th>

                          <th>{t("billing.table.document")}</th>

                          <th>{t("billing.table.type")}</th>

                          <th>{t("billing.document.description")}</th>

                          <th>Debit</th>

                          <th>Credit</th>

                          <th>{t("billing.table.status")}</th>
                        </tr>
                      </thead>

                      <tbody>
                        {report.rows.map((row, index) => {
                          const rowStatus = String(
                            row.status || "",
                          ).toLowerCase();

                          return (
                            <tr key={`${row.document}-${index}`}>
                              <td>{formatDate(row.date)}</td>

                              <td>
                                <strong>{row.document}</strong>
                              </td>

                              <td>{row.type}</td>

                              <td>{row.description}</td>

                              <td>{formatMoney(row.debit)}</td>

                              <td>{formatMoney(row.credit)}</td>

                              <td>
                                <span className={getStatusClass(rowStatus)}>
                                  {getStatusLabel(rowStatus)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState title={t("billing.reports.noReport")} />
                )}
              </section>
            )}

            {/* Report Summary */}

            <section className="billing-panel billing-report-summary">
              <div>
                <span>{t("billing.reports.totalOutstanding")}</span>

                <strong>
                  {formatMoney(summary?.totalOutstanding, summary?.currency)}
                </strong>
              </div>

              <div>
                <span>{t("billing.reports.paidAmount")}</span>

                <strong>
                  {formatMoney(summary?.thisMonth?.paid, summary?.currency)}
                </strong>
              </div>

              <div>
                <span>{t("billing.status.overdue")}</span>

                <strong>
                  {formatMoney(summary?.overdue, summary?.currency)}
                </strong>
              </div>
            </section>
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Details Modal                                                      */}
      {/* ------------------------------------------------------------------ */}

      {selectedDocument && (
        <div
          className="billing-modal-backdrop"
          onClick={() => setSelectedDocument(null)}
        >
          <div
            className="billing-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="billing-modal-header">
              <div>
                <span className="billing-modal-type">
                  {selectedDocument.type === "invoice"
                    ? t("billing.modal.invoiceDetails")
                    : t("billing.modal.proformaDetails")}
                </span>

                <h2>{selectedDocument.number}</h2>
              </div>

              <button
                type="button"
                className="billing-modal-close"
                onClick={() => setSelectedDocument(null)}
                aria-label={t("billing.actions.close")}
              >
                <CloseIcon />
              </button>
            </div>

            <div className="billing-modal-status-row">
              <span className={getStatusClass(selectedDocument.status)}>
                {getStatusLabel(selectedDocument.status)}
              </span>

              <strong>
                {formatMoney(selectedDocument.total, selectedDocument.currency)}
              </strong>
            </div>

            {/* Detail grid */}

            <div className="billing-detail-grid">
              <div>
                <span>{t("billing.document.issueDate")}</span>

                <strong>{formatDate(selectedDocument.issueDate)}</strong>
              </div>

              <div>
                <span>{t("billing.document.dueDate")}</span>

                <strong>{formatDate(selectedDocument.dueDate)}</strong>
              </div>

              <div>
                <span>{t("billing.document.customer")}</span>

                <strong>{selectedDocument.customer || "—"}</strong>
              </div>

              <div>
                <span>{t("billing.document.currency")}</span>

                <strong>{selectedDocument.currency || "—"}</strong>
              </div>
            </div>

            {/* Description */}

            <div className="billing-modal-description">
              <span>{t("billing.document.description")}</span>

              <p>
                {selectedDocument.description ||
                  t("billing.document.noDescription")}
              </p>
            </div>

            {/* Items */}

            <div className="billing-items">
              <div className="billing-items-header">
                <span>{t("billing.document.items.description")}</span>

                <span>{t("billing.document.items.quantity")}</span>

                <span>{t("billing.document.items.price")}</span>

                <span>{t("billing.document.items.total")}</span>
              </div>

              {(selectedDocument.items || []).map((item, index) => (
                <div className="billing-item-row" key={index}>
                  <span>{item.description || "—"}</span>

                  <span>{item.quantity ?? "—"}</span>

                  <span>
                    {formatMoney(item.unitPrice, selectedDocument.currency)}
                  </span>

                  <strong>
                    {formatMoney(item.total, selectedDocument.currency)}
                  </strong>
                </div>
              ))}

              <div className="billing-total-row">
                <span>{t("billing.document.subtotal")}</span>

                <strong>
                  {formatMoney(
                    selectedDocument.subtotal,
                    selectedDocument.currency,
                  )}
                </strong>
              </div>

              <div className="billing-total-row">
                <span>{t("billing.document.tax")}</span>

                <strong>
                  {formatMoney(selectedDocument.tax, selectedDocument.currency)}
                </strong>
              </div>

              <div className="billing-total-row billing-total-final">
                <span>{t("billing.document.total")}</span>

                <strong>
                  {formatMoney(
                    selectedDocument.total,
                    selectedDocument.currency,
                  )}
                </strong>
              </div>

              {Number(selectedDocument.paid || 0) > 0 && (
                <div className="billing-total-row">
                  <span>{t("billing.document.paid")}</span>

                  <strong>
                    {formatMoney(
                      selectedDocument.paid,
                      selectedDocument.currency,
                    )}
                  </strong>
                </div>
              )}

              {Number(selectedDocument.balance || 0) > 0 && (
                <div className="billing-total-row">
                  <span>{t("billing.document.outstanding")}</span>

                  <strong>
                    {formatMoney(
                      selectedDocument.balance,
                      selectedDocument.currency,
                    )}
                  </strong>
                </div>
              )}
            </div>

            {/* Actions */}

            <div className="billing-modal-actions">
              <button
                type="button"
                className="billing-btn billing-btn-secondary"
                onClick={() => setSelectedDocument(null)}
              >
                {t("billing.actions.close")}
              </button>

              {(selectedDocument.status === "open" ||
                selectedDocument.status === "overdue" ||
                selectedDocument.status === "unpaid") && (
                <button
                  type="button"
                  className="billing-btn billing-btn-primary"
                  onClick={() => goToWallet(selectedDocument)}
                >
                  <AccountBalanceWalletIcon fontSize="small" />

                  <span>{t("billing.actions.payViaWallet")}</span>

                  <ArrowForwardIcon fontSize="small" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BillingPayments;
